import React, { useState, useMemo } from "react";
import Layout from "../../components/common/Layout";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";

const PROBATION_MONTHS = 3;

// Returns true once the employee's 3-month probation period is over.
// Prefers an explicit `probationCompleted` flag if your userProfile
// already tracks that; otherwise derives it from a joining/hire date.
// Adjust the field names below to match your actual schema.
function isProbationComplete(userProfile) {
  if (!userProfile) return false;

  if (typeof userProfile.probationCompleted === "boolean") {
    return userProfile.probationCompleted;
  }

  const joinDateRaw =
    userProfile.joiningDate ||
    userProfile.dateOfJoining ||
    userProfile.hireDate ||
    userProfile.startDate;

  if (!joinDateRaw) return false; // no data → treat as not eligible yet

  const joinDate =
    typeof joinDateRaw?.toDate === "function"
      ? joinDateRaw.toDate() // Firestore Timestamp
      : new Date(joinDateRaw);

  if (isNaN(joinDate.getTime())) return false;

  const eligibleDate = new Date(joinDate);
  eligibleDate.setMonth(eligibleDate.getMonth() + PROBATION_MONTHS);

  return new Date() >= eligibleDate;
}

export default function TravelReimbursement() {
  const { userProfile, currentUser } = useAuth();
  const [myClaims, setMyClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [historyOpened, setHistoryOpened] = useState(false);

  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);

  // Only full-time employees are eligible for travel reimbursement claims.
  const isFullTime = userProfile?.employmentType === "full-time";

  // Button is only enabled once the employee is full-time AND past probation.
  const canCreateClaim = useMemo(
    () => isFullTime && isProbationComplete(userProfile),
    [userProfile, isFullTime]
  );

  // Tooltip text explaining why the button is disabled, prioritizing the
  // employment-type restriction since it's the harder blocker.
  const disabledReason = !isFullTime
    ? "Only available to full-time employees"
    : "Available after completing your 3-month probation period";

  const fetchClaims = async () => {
    if (!currentUser?.uid) return;
    setLoadingClaims(true);
    try {
      // Use only where() — no orderBy() — to avoid requiring a composite
      // Firestore index. Sort client-side instead.
      const q = query(
        collection(db, "reimbursements"),
        where("userId", "==", currentUser.uid)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by submittedAt descending (client-side)
      docs.sort((a, b) => {
        const ta = a.submittedAt?.toMillis?.() ?? 0;
        const tb = b.submittedAt?.toMillis?.() ?? 0;
        return tb - ta;
      });
      setMyClaims(docs);
    } catch (e) {
      console.error("Error fetching claims", e);
    }
    setLoadingClaims(false);
  };

  const handleViewHistory = () => {
    setHistoryOpened(true);
    fetchClaims();
  };

  const handleCloseHistory = () => {
    setHistoryOpened(false);
  };

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="page-title">Travel Reimbursements</h1>
        <div style={{ display: "flex", gap: 10 }}>
          {!historyOpened && (
            <button
              onClick={handleViewHistory}
              className="btn-success-sm"
              style={{ padding: "8px 16px", fontSize: "14px", cursor: "pointer" }}
            >
              View Claim History
            </button>
          )}

          {canCreateClaim ? (
            <Link
              to="/travel-reimbursement/new"
              className="btn-success-sm"
              style={{ textDecoration: "none", padding: "8px 16px", fontSize: "14px" }}
            >
              + Create New Claim
            </Link>
          ) : (
            <button
              disabled
              title={disabledReason}
              className="btn-success-sm"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                cursor: "not-allowed",
                opacity: 0.5,
                pointerEvents: "none",
              }}
            >
              + Create New Claim
            </button>
          )}
        </div>
      </div>

      {historyOpened && (
        <div style={{ marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#164231" }}>Claim History</h3>
            <button
              onClick={handleCloseHistory}
              title="Close"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 20,
                lineHeight: 1,
                color: "#64748B",
                padding: "2px 8px",
                borderRadius: 6,
              }}
            >
              ×
            </button>
          </div>
          <div className="data-table-wrap">
            {loadingClaims ? (
              <div className="card-loading">Loading your claims...</div>
            ) : myClaims.length === 0 ? (
              <div className="card-empty">You have no submitted claims.</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Total Amount</th>
                    <th>Advance</th>
                    <th>Reimbursement</th>
                  </tr>
                </thead>
                <tbody>
                  {myClaims.map(c => (
                    <tr key={c.id}>
                      <td>{c.date}</td>
                      <td>₹{parseFloat(c.grandTotal + (c.advance || 0)).toFixed(2)}</td>
                      <td>₹{parseFloat(c.advance || 0).toFixed(2)}</td>
                      <td><strong>₹{parseFloat(c.grandTotal || 0).toFixed(2)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}